import { NavLink, Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Briefcase, Wrench, PackageOpen, DollarSign,
  FileText, LogOut, ShieldCheck, Layers, MessageSquare, Settings,
  Wallet, BarChart3, X
} from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { formatRole } from "@/lib/utils";

const itemsFor = (role) => {
  const base = [
    { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["super_admin", "business_admin", "analyst_admin"] },
    { to: "/admin/jobs", label: "Job Management", icon: Briefcase, roles: ["super_admin"] },
    { to: "/admin/workstation", label: "Workstation", icon: Wrench, roles: ["super_admin", "analyst_admin"] },
    { to: "/admin/users", label: "Users", icon: Users, roles: ["super_admin"] },
    { to: "/admin/plans", label: "Plans", icon: DollarSign, roles: ["super_admin", "business_admin"] },
    { to: "/admin/modules", label: "Modules", icon: PackageOpen, roles: ["super_admin"] },
    { to: "/admin/credit-ledger", label: "Credit Ledger", icon: Wallet, roles: ["super_admin", "business_admin"] },
    { to: "/admin/reports", label: "Reports", icon: BarChart3, roles: ["super_admin", "business_admin"] },
    { to: "/admin/messages", label: "Contact Msgs", icon: MessageSquare, roles: ["super_admin", "business_admin"] },
    { to: "/admin/audit", label: "Audit Logs", icon: FileText, roles: ["super_admin"] },
    { to: "/admin/settings", label: "Settings", icon: Settings, roles: ["super_admin", "business_admin", "analyst_admin"] },
  ];
  return base.filter((i) => i.roles.includes(role));
};

export default function AdminSidebar({ open, onClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = () => {
    onClose?.();
    logout();
    navigate("/");
  };
  const items = itemsFor(user?.role);

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`
      fixed top-0 left-0 z-50
      w-64 h-screen bg-white border-r border-[#E5E7EB]
      flex flex-col
      transform transition-transform duration-300
      ${open ? "translate-x-0" : "-translate-x-full"}
      lg:translate-x-0
    `}
      >
        <div className="h-16 border-b border-[#E5E7EB] px-5 flex items-center justify-between">
          <a href="https://bioinfo.bionivid.in" className="cursor-pointer">
            <Logo />
          </a>

          <button
            onClick={onClose}
            className="lg:hidden p-1 rounded hover:bg-gray-100"
          >
            <X size={22} />
          </button>
        </div>
        <div className="px-5 py-3 border-b border-[#E5E7EB]">
          <span className="pill bg-[#0B6936] text-white border-[#0B6936]/30">
            <ShieldCheck size={12} /> {formatRole(user?.role).toUpperCase()}
          </span>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                onClick={onClose}
                data-testid={`admin-sidebar-${it.to.split("/").pop()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${isActive
                    ? "bg-[#EAF4E3] text-[#0B6936]"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`
                }
              >
                <Icon size={18} strokeWidth={1.75} />
                {it.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-[#E5E7EB]">
          <div className="px-3 py-2 mb-2">
            <div className="text-sm font-semibold text-gray-900 truncate">{user?.full_name}</div>
            <div className="text-xs text-gray-500 truncate">{user?.email}</div>
          </div>
          <button
            onClick={doLogout}
            data-testid="admin-logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900"
          >
            <LogOut size={16} /> Log out
          </button>
        </div>
      </aside>
    </>
  )
}
